import { IApiClient } from "@common/network/interfaces";
import store from "@store/index";
import {
  IAddFormAnalyticPayload,
  IAddNewFormActionPayload,
  IFetchFormsActionPayload,
  IFormServer,
  IInfographicDataByQueryPayload,
  IInfographicsDashboardDataServer,
} from "@common/redux/forms/interface";
import { ApiClient } from "@network/client";
import { getConfig } from "@config/index";
import {
  IAdvancedRulesServer,
  IFormLockData,
  IFormLockPayload,
} from "@common/redux/formBuilder/interface";
import {
  fetchForm,
  formAction,
  formAnalytic,
  formAnalyticAction,
  formDashboard,
  formDashboardAction,
  formQuery,
  formRule,
  fetchRuleAction,
  getSubmissionCount,
  getSubmissionsForLookup,
  publicFormAction,
  deleteRuleAction,
  formPermission,
  fetchWorkflow,
  getSubmissionsForLookupByQuery,
  formSubmissionQuery,
  publicFormSubmissionQuery,
  getSignatureById,
  createWorkflow,
  formLockAction,
  getFilters,
} from "../apis/urls";

const ENDPOINTS = {
  fetchForms: () => fetchForm,
  updateForms: () => formAction,
  fetchPublicForm: () => publicFormAction,
  addNewForm: () => formAction,
  deleteForm: () => formAction,
  fetchFormById: () => formAction,
  fetchPermisionById: () => formPermission,
  fetchFormRule: () => fetchRuleAction,
  fetchWorkflow: () => fetchWorkflow,
  updateFormRule: () => formRule,
  deleteFormRules: () => deleteRuleAction,
  createFormRule: () => formRule,
  fetchInfographicsDashboardDataById: () => formDashboardAction,
  updateInfographicsDashboardDataById: () => formDashboard,
  addInfographicsDashboardDataById: () => formDashboard,
  deleteInfographicsDashboardDataByFormId: () => formDashboardAction,
  addInfographicsAnalytics: () => formAnalytic,
  getInfographicsAnalytics: () => formAnalyticAction,
  getSubmissionCount: () => getSubmissionCount,
  getInfographicsDataByQuery: () => formQuery,
  getSubmissionsForLookup: () => getSubmissionsForLookup,
  getSubmissionsForLookupByQuery: () => getSubmissionsForLookupByQuery,
  getSubmissionsForLookupByQueryWithFilter: () => formSubmissionQuery,
  getSubmissionsForLookupByQueryWithFilterPublicApi: () =>
    publicFormSubmissionQuery,
  getSignatureDetailsById: () => getSignatureById,
  createWorkflow: () => createWorkflow,
  addFormLockData: (formId) => `${formLockAction}/${formId}/lock`,
  getPageDataEndPoint: () => getFilters,
};
class FormsRepository {
  private apiClient: IApiClient;
  constructor(apiClient: IApiClient) {
    this.apiClient = apiClient;
  }

  public getPageData = async (applicationId: string) => {
    const params = { applicationId: applicationId, type: "PAGE" };

    const result = await this.apiClient.get(
      ENDPOINTS.getPageDataEndPoint(),
      params,
      {
        baseURL: getConfig().APP_BE_BASE_URL,
      }
    );
    return result.data;
  };

  public updateFormRules = async (formRuleData: IAdvancedRulesServer) => {
    const payload = formRuleData;
    const result = await this.apiClient.put(
      ENDPOINTS.updateFormRule(),
      payload,
      {},
      {
        baseURL: getConfig().APP_BE_BASE_URL,
      }
    );
    return result.data;
  };

  public updateForms = async (formData: IFormServer) => {
    const payload = formData;
    const result = await this.apiClient.put(
      ENDPOINTS.updateForms(),
      payload,
      {},
      {
        baseURL: getConfig().APP_BE_BASE_URL,
      }
    );
    return result.data;
  };

  public addNewForm = async (emptyForm: IAddNewFormActionPayload) => {
    const payload = emptyForm;
    const result = await this.apiClient.post(
      ENDPOINTS.addNewForm(),
      payload,
      {},
      {
        baseURL: getConfig().APP_BE_BASE_URL,
      }
    );
    return result.data;
  };

  public deleteForm = async (formId: string) => {
    const params = {
      id: formId,
    };
    const result = await this.apiClient.delete(
      ENDPOINTS.deleteForm(),
      {},
      params,
      {
        baseURL: getConfig().APP_BE_BASE_URL,
      }
    );
    return result.data;
  };

  public deleteFormRules = async (formId: string) => {
    const result = await this.apiClient.delete(
      ENDPOINTS.deleteFormRules() + formId,
      {},
      {},
      {
        baseURL: getConfig().APP_BE_BASE_URL,
      }
    );
    return result.data;
  };

  public fetchForms = async (data: IFetchFormsActionPayload) => {
    const params = data;
    const result = await this.apiClient.get(ENDPOINTS.fetchForms(), params, {
      baseURL: getConfig().APP_BE_BASE_URL,
    });
    return result.data;
  };

  public fetchFormByIds = async (formIds: string[]) => {
    const formIdsToString: string = formIds.toString(); //to convert list of ids to string
    const isTokenAvailabe = store.getState().auth.isAuthenticated;
    const param = {
      id: formIdsToString,
    };
    const result = await this.apiClient.get(
      isTokenAvailabe ? ENDPOINTS.fetchFormById() : ENDPOINTS.fetchPublicForm(),
      param,
      {
        baseURL: getConfig().APP_BE_BASE_URL,
      }
    );
    return result.data;
  };

  public fetchPermissionById = async (roleName?: string) => {
    try {
      const param = {
        // applicationId: id,
      };
      const result = await this.apiClient.get(
          `${ENDPOINTS.fetchPermisionById()}${
          roleName ? `?roleName=${roleName}` : ""
        }`,
        {},
        {
          baseURL: getConfig().APP_BE_BASE_URL,
        }
      );
      return result.data;
    } catch (error) {
      console.log(error, "error");
    }
  };
  public createFormRules = async (formRuleData: IAdvancedRulesServer) => {
    const payload = formRuleData;
    const result = await this.apiClient.post(
      ENDPOINTS.createFormRule(),
      payload,
      {},
      {
        baseURL: getConfig().APP_BE_BASE_URL,
      }
    );
    return result.data;
  };
  public fetchFormRules = async (formId: string) => {
    const result = await this.apiClient.get(
      ENDPOINTS.fetchFormRule() + formId,
      {},
      {
        baseURL: getConfig().APP_BE_BASE_URL,
      }
    );
    return result.data;
  };

  public fetchWorkflows = async (formId: string) => {
    const result = await this.apiClient.get(
      ENDPOINTS.fetchWorkflow() + formId,
      {},
      {
        baseURL: getConfig().APP_BE_BASE_URL,
      }
    );
    return result.data;
  };

  public fetchFormDashBoardDataById = async (formId: string) => {
    const result = await this.apiClient.get(
      ENDPOINTS.fetchInfographicsDashboardDataById() + formId,
      {},
      {
        baseURL: getConfig().APP_BE_BASE_URL, //hardcoded value have to make Config
      }
    );
    return result.data;
  };

  public updateFormDashBoardDataById = async (
    infographicsData: IInfographicsDashboardDataServer
  ) => {
    const payload = infographicsData;
    const result = await this.apiClient.put(
      ENDPOINTS.updateInfographicsDashboardDataById(),
      payload,
      {},
      {
        baseURL: getConfig().APP_BE_BASE_URL, //hardcoded value have to make Config
      }
    );
    return result.data;
  };

  public addFormDashBoardDataById = async (
    infographicsData: IInfographicsDashboardDataServer
  ) => {
    const payload = infographicsData;
    const result = await this.apiClient.post(
      ENDPOINTS.addInfographicsDashboardDataById(),
      payload,
      {},
      {
        baseURL: getConfig().APP_BE_BASE_URL, //hardcoded value have to make Config
      }
    );
    return result.data;
  };

  public deleteFormDashBoardDataByFormId = async (fromId: string) => {
    const result = await this.apiClient.delete(
      ENDPOINTS.deleteInfographicsDashboardDataByFormId() + fromId,
      {},
      {},
      {
        baseURL: getConfig().APP_BE_BASE_URL,
      }
    );
    return result.data;
  };

  public addInfographicsAnalytics = async (
    formAnalyticData: IAddFormAnalyticPayload
  ) => {
    const payload = formAnalyticData;
    const result = await this.apiClient.post(
      ENDPOINTS.addInfographicsAnalytics(),
      payload,
      {},
      {
        baseURL: getConfig().APP_BE_BASE_URL, //hardcoded value have to make Config
      }
    );
    return result.data;
  };

  public getInfographicsAnalytics = async (formId: string) => {
    const result = await this.apiClient.get(
      ENDPOINTS.getInfographicsAnalytics() + formId,
      {},
      {
        baseURL: getConfig().APP_BE_BASE_URL,
      }
    );
    return result.data;
  };

  public getInfographicsDataByQuery = async (
    payload: IInfographicDataByQueryPayload
  ) => {
    const result = await this.apiClient.post(
      ENDPOINTS.getInfographicsDataByQuery(),
      payload,
      {},
      {
        baseURL: getConfig().APP_BE_BASE_URL,
      }
    );
    return result.data;
  };

  public getSubmissionCount = async (formIds: string[]) => {
    function generateIdString(formIds) {
      const idString = formIds.join("%2C");
      return idString;
    }
    const result = await this.apiClient.get(
      ENDPOINTS.getSubmissionCount() +
        `?formIdList=${generateIdString(formIds)}`,
      {},
      {
        baseURL: getConfig().APP_BE_BASE_URL,
      }
    );
    return result.data;
  };

  public fetchOptionsForLookupField = async (
    fieldIds: string[],
    formId: string
  ) => {
    const params = {
      formId: formId,
      fieldIds: fieldIds,
    };
    const result = await this.apiClient.get(
      ENDPOINTS.getSubmissionsForLookup(),
      params,
      {
        baseURL: getConfig().APP_BE_BASE_URL,
      }
    );
    return result.data;
  };

  // public fetchOptionsForLookupFieldByQuery = async (
  //   query: any,
  // ) => {
  //   const payload = query;
  //   const params = {
  //     pageNumber:0,
  //     pageSize: 100,
  //     filter:filter,
  //   };
  //   const result = await this.apiClient.put(
  //     ENDPOINTS.getSubmissionsForLookupByQuery(),
  //     {payload},
  //     params,
  //     {
  //       baseURL: getConfig().APP_BE_BASE_URL,
  //     }
  //   );
  //   return result.data;
  // };

  public fetchOptionsForLookupFieldByFIlter = async (
    formId: string,
    filter?: string
  ) => {
    const params = {
      filter: filter,
      pageNumber: 0,
      pageSize: 100,
      formId: formId,
    };
    const result = await this.apiClient.get(
      ENDPOINTS.getSubmissionsForLookupByQuery(),
      params,
      {
        baseURL: getConfig().APP_BE_BASE_URL,
      }
    );
    return result.data;
  };
  public fetchOptionsForLookupFieldWithFilter = async (payload) => {
    const pazeSize = payload?.pageSize ?? 100;
    const params = {
    pageNumber: payload?.pageNumber ?? 0,
      pageSize: pazeSize,
    };
    const updatedPayload = {
      ...payload,
      excludeRelatedSubmissions: true,
    };
    const result = await this.apiClient.post(
      ENDPOINTS.getSubmissionsForLookupByQueryWithFilter(),
      updatedPayload,
      params,
      {
        baseURL: getConfig().APP_BE_BASE_URL,
      }
    );
    return result.data;
  };

  public fetchOptionsForLookupFieldWithFilterOfPublicApi = async (payload) => {
    const pazeSize = payload?.pageSize ?? 100;
    const params = {
      pageNumber: 0,
      pageSize: pazeSize,
      orgId: payload?.orgId,
    };
    const updatedPayload = {
      formId: payload?.formId,
      query: payload?.query,
      excludeRelatedSubmissions: true
    };
    const result = await this.apiClient.post(
      ENDPOINTS.getSubmissionsForLookupByQueryWithFilterPublicApi(),
      updatedPayload,
      params,
      {
        baseURL: getConfig().APP_BE_BASE_URL,
      }
    );
    return result.data;
  };

  public getSignatureDetailsById = async (signatureIdsString: string) => {
    const result = await this.apiClient.get(
      ENDPOINTS.getSignatureDetailsById() + `?filePath=${signatureIdsString}`,
      "",
      {
        baseURL: getConfig().APP_BE_BASE_URL,
      }
    );
    return await result;
  };

  public createWorkflow = async (workflowData: any) => {
    const payload = workflowData;
    const result = await this.apiClient.post(
      ENDPOINTS.createWorkflow(),
      payload,
      {},
      {
        baseURL: getConfig().APP_BE_BASE_URL,
      }
    );
    return result.data;
  };

  public updateFormLockData = async (
    formId: string,
    formLockData: IFormLockData
  ) => {
    const isFormLocked = formLockData?.isFormLocked[formId];
    if (isFormLocked == undefined || isFormLocked == null) return;
    const result = await this.apiClient.put(
      ENDPOINTS.addFormLockData(formId) + `?isFormLocked=${isFormLocked}`,
      formLockData,
      {},
      {
        baseURL: getConfig().APP_BE_BASE_URL,
      }
    );
    return result.data;
  };
}

const formsRepository = new FormsRepository(ApiClient);

export { formsRepository as FormsRepository };
